'use client';

import React, { useCallback, useState } from 'react';
import {
  CRMOwners,
  CRMStatuses,
  Drawer,
  EncounterTags,
  Fellowship,
  GPOAffiliations,
  HospitalSystems,
  Independent,
  Inpatient,
  MedicalSchools,
  OpenPayments,
  PayerType,
  ProviderLists,
  Residency,
  SiteOfCare,
  SiteSpecialties,
  Specialties,
  UnsavedChangesDialog,
} from 'src/components';
import { IconButton } from 'src/components/PulseUI/IconButton';
import { LOCATION } from 'src/hooks/filters/constants';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useFilterDrawer } from 'src/hooks/drawers/useFilterDrawer';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import useUserSettings from 'src/hooks/users/useUserSettings';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { PresetsDropdown } from '../PresetsDropdown';
import { useDispatch } from 'react-redux';
import { discoveryApi } from 'src/api/redux';

type DrawerSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'base' | 'base2';
type DrawerVariant = 'temporary' | 'persistent';
type DrawerAnchor = 'left' | 'right' | 'top' | 'bottom';

interface AllFiltersDrawerProps {
  hideBackdrop?: boolean;
  variant?: DrawerVariant;
  anchor?: DrawerAnchor;
  size?: DrawerSize;
}

const AllFiltersDrawer = ({
  hideBackdrop = false,
  anchor = 'right',
  variant = 'temporary',
  size = 'base2',
  ...rest
}: AllFiltersDrawerProps) => {
  const dispatch = useDispatch();
  const { isDrawerOpen, closeAllButCurrent } = useDrawer();
  const { filterSet, clearAllFilters, applyFilters, resetToApplied, isDirty } =
    useFilterSets(LOCATION.DISCOVERY);
  const { toggleSaveFilterModal, setSaveFilterType } = useFilterDrawer();
  const { settings: userSettings } = useUserSettings();

  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const isProvider = userSettings?.prospectType === 'HCP';

  const invalidateDiscoveryCache = useCallback(() => {
    dispatch(discoveryApi.util.invalidateTags(['Discovery']));
  }, [dispatch]);

  const handleClearFilters = useCallback(() => {
    clearAllFilters('FILTER');
    invalidateDiscoveryCache();
  }, [clearAllFilters, invalidateDiscoveryCache]);

  const handleManageFilters = useCallback(() => {
    closeAllButCurrent('manageFiltersDrawer');
  }, [closeAllButCurrent]);

  const handleApplyFilters = useCallback(() => {
    applyFilters();
    closeAllButCurrent('resultsDrawer');
    invalidateDiscoveryCache();
  }, [applyFilters, closeAllButCurrent, invalidateDiscoveryCache]);

  const handleClose = useCallback(() => {
    closeAllButCurrent('resultsDrawer');
  }, [closeAllButCurrent]);

  const handleCancel = useCallback(() => {
    resetToApplied();
    handleClose();
  }, [handleClose, resetToApplied]);

  const handleBeforeClose = useCallback(() => {
    if (isDirty()) {
      setShowDiscardDialog(true);
    } else {
      handleCancel();
    }
  }, [isDirty, handleCancel]);

  const handleDiscardConfirm = useCallback(() => {
    setShowDiscardDialog(false);
    handleCancel();
  }, [handleCancel]);

  // Check if any filters have values
  const hasAnyFilters =
    filterSet?.siteOfCare?.values?.length > 0 ||
    filterSet?.payerType?.values?.length > 0 ||
    filterSet?.openPayments?.values?.length > 0 ||
    filterSet?.specialties?.values?.length > 0 ||
    filterSet?.siteSpecialties?.values?.length > 0 ||
    filterSet?.hospitalSystem?.values?.length > 0 ||
    filterSet?.gpoAffiliations?.values?.length > 0 ||
    filterSet?.medicalSchools?.values?.length > 0 ||
    filterSet?.residencies?.values?.length > 0 ||
    filterSet?.fellowships?.values?.length > 0 ||
    filterSet?.tags?.values?.length > 0 ||
    filterSet?.providerLists?.values?.length > 0 ||
    filterSet?.crmOwners?.values?.length > 0 ||
    filterSet?.crmStatuses?.values?.length > 0 ||
    filterSet?.inpatient?.values?.length > 0 ||
    filterSet?.independent?.values?.length > 0;

  return (
    <>
    <Drawer
      isOpen={isDrawerOpen('allFiltersDrawer')}
      onClose={handleClose}
      hideBackdrop={hideBackdrop}
      anchor={anchor}
      variant={variant}
      size={size}
      onBeforeClose={handleBeforeClose}
      {...rest}
    >
      <Drawer.Header className="bg-sky-100 text-primary border-b-0">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <p className="font-bold text-lg">Filters</p>
          </div>
          <div className="flex items-center gap-2">
            <IconButton
              variant="plain"
              color="neutral"
              size="sm"
              onClick={handleCancel}
              icon={faXmark}
              className="text-primary hover:bg-sky-200"
            />
          </div>
        </div>
      </Drawer.Header>

      <Drawer.Content className="flex flex-col overflow-hidden">
        {/* Presets section - fixed */}
        <div className="self-stretch p-6 bg-white inline-flex flex-col justify-start items-start gap-2">
          <div className="self-stretch inline-flex justify-center items-center gap-2">
            <div className="flex-1 justify-start text-primary text-base font-bold font-['Inter'] leading-normal">
              Presets
            </div>
            <div
              className="justify-start text-tertiary text-xs font-normal font-['Inter']
                      underline leading-none cursor-pointer hover:text-neutral-600"
              onClick={handleManageFilters}
            >
              Manage
            </div>
          </div>
          <PresetsDropdown type="FILTER" />
        </div>

        {/* Filters section - scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="flex justify-end items-center w-full pr-6">
            <div
              className="text-tertiary text-xs font-normal font-['Inter']
                        underline leading-none cursor-pointer hover:text-neutral-600"
              onClick={handleClearFilters}
            >
              Clear all
            </div>
          </div>
          {/* Claims Filters */}
          <div className="w-full px-6 py-4 inline-flex flex-col gap-4">
            <SiteOfCare location={LOCATION.DISCOVERY} />
            <Inpatient location={LOCATION.DISCOVERY} />
            <PayerType location={LOCATION.DISCOVERY} />
          </div>

          {/* Provider/Site Filters */}
          <div className="px-6 pb-6 inline-flex flex-col justify-start items-start gap-4">
            <div className="flex flex-col justify-start items-start gap-6">
              {isProvider && <Specialties location={LOCATION.DISCOVERY} />}
              {!isProvider && (
                <SiteSpecialties location={LOCATION.DISCOVERY} />
              )}
              <Independent location={LOCATION.DISCOVERY} />
              <HospitalSystems location={LOCATION.DISCOVERY} />
              <GPOAffiliations location={LOCATION.DISCOVERY} />
              <OpenPayments location={LOCATION.DISCOVERY} />
              {isProvider && (
                <>
                  <MedicalSchools location={LOCATION.DISCOVERY} />
                  <Residency location={LOCATION.DISCOVERY} />
                  <Fellowship location={LOCATION.DISCOVERY} />
                </>
              )}
              <EncounterTags location={LOCATION.DISCOVERY} />
              <ProviderLists location={LOCATION.DISCOVERY} />
              <CRMOwners location={LOCATION.DISCOVERY} />
              <CRMStatuses location={LOCATION.DISCOVERY} />
            </div>
          </div>
        </div>
      </Drawer.Content>

      <Drawer.Footer className="!p-0 w-full bg-white border-t border-neutral-300 inline-flex flex-col justify-center items-start">
        <div className="w-full h-20 p-4 bg-white inline-flex justify-start items-center gap-4">
          <div
            className={`flex-1 px-4 py-2 rounded-full flex justify-center items-center gap-2.5
              ${
                hasAnyFilters
                  ? 'cursor-pointer bg-sky-50 hover:bg-sky-100'
                  : 'pointer-events-none bg-neutral-50'
              }
              `}
            onClick={() => {
              setSaveFilterType('FILTER');
              toggleSaveFilterModal();
            }}
          >
            <div className="flex justify-center items-center gap-1">
              <div
                className={`justify-start text-base font-semibold font-['Inter'] leading-normal
                ${hasAnyFilters ? 'text-sky-900' : 'text-neutral-300'}
                `}
              >
                Save New Preset
              </div>
            </div>
          </div>
          <div
            className="flex-1 px-4 py-2 bg-sky-600 rounded-full flex justify-center items-center gap-2.5 cursor-pointer hover:bg-sky-800"
            onClick={handleApplyFilters}
          >
            <div className="flex justify-center items-center gap-1">
              <div className="justify-start text-white text-base font-semibold font-['Inter'] leading-normal">
                Apply
              </div>
            </div>
          </div>
        </div>
      </Drawer.Footer>
    </Drawer>
    <UnsavedChangesDialog
      isOpen={showDiscardDialog}
      onDiscard={handleDiscardConfirm}
      onCancel={() => setShowDiscardDialog(false)}
    />
  </>
  );
};

export default AllFiltersDrawer;
